import '../../css/app.css'
import '../../css/common.css'
import '../../css/main.css'
import Page from '../../Page';
import React, { useCallback } from 'react';
import { useState, useEffect, useRef } from 'react';

import { ChevronDown, FileX } from 'lucide-react';
import { useSelector } from 'react-redux';
import { SyncLoader } from "react-spinners";

function List({
    tableHeaderData,
    tableBodyData,
    filter_tableBodyData = [],
    page, // ★ 여기서 page를 전달받음
    setPage,
    maxFirstPage,
    onInputChange,
    height,
    nonePage = true,

    // 체크박스 관련
    checkedRows = [],
    onCheckboxChange = () => { },

    // 배경색 변경 조건
    check50 = false,
    check100 = false,
    check200 = false,
    clickData,
    onRowClick,
    listType = false,
    listState = false,
    scrollState = false,
    spinner = false,

    observerRef,
}) {
    // 헤더 컨테이너 참조
    const headerRef = useRef(null);
    // 바디 컨테이너 참조
    const bodyRef = useRef(null);

    const [Width, setWidth] = useState("0");
    const [tableWidth, setTableWidth] = useState("0");
    const [extraWidth, setExtraWidth] = useState(0);
    const [coordinate, setCoordinate] = useState(0);
    const [ytop, setYtop] = useState(0);

    // 스크롤 여부 체크용
    const [isScrollable, setIsScrollable] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    // (1) 윈도우 리사이즈
    useEffect(() => {
        const handleResize = () => {
            if (bodyRef.current) {
                setWidth(`${bodyRef.current.offsetWidth}`);
            }
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // (2) 테이블 폭 계산
    useEffect(() => {
        if (
            Width !== "0" &&
            tableWidth !== "0" &&
            !isNaN(Width) &&
            !isNaN(tableWidth)
        ) {
            if (Number(Width) > Number(tableWidth)) {
                setExtraWidth((Number(Width) - Number(tableWidth)) / tableHeaderData.length);
            } else {
                setExtraWidth(0);
            }
        }
    }, [Width, tableWidth, tableHeaderData]);

    // (3) listState 변경 => 위치 업데이트
    useEffect(() => {
        if (bodyRef.current) {
            setCoordinate(bodyRef.current.getBoundingClientRect().top + 46);
            setYtop(bodyRef.current.getBoundingClientRect().top);
        }
    }, [listState]);

    const onClickCoordinate = () => {
        if (bodyRef.current) {
            setYtop(bodyRef.current.getBoundingClientRect().top);
        }
    };

    // (4) 스크롤 가능 여부
    const checkScrollable = useCallback(() => {
        if (!bodyRef.current) return;
        const el = bodyRef.current;
        // 세로 스크롤이 필요한지 여부
        setIsScrollable(el.scrollHeight > el.clientHeight);

        // "현재 스크롤이 맨 아래인지" 다시 판별
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
            setAtBottom(true);
        } else {
            setAtBottom(false);
        }
    }, []);

    // (4)-1. 마운트 / filter_tableBodyData 변경 시 체크
    useEffect(() => {
        checkScrollable();
    }, [filter_tableBodyData, tableBodyData, checkScrollable]);

    // (5) 맨 아래 스크롤 이벤트
    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        const handleScroll = () => {
            // 세로스크롤 맨 아래 판별
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
                setAtBottom(true);
            } else {
                setAtBottom(false);
            }
            // 헤더와 가로스크롤 동기화
            if (headerRef.current) {
                headerRef.current.scrollLeft = el.scrollLeft;
            }
        };
        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    // (6) 검색 후 page=1이면, 스크롤을 맨 위로 이동
    useEffect(() => {
        if (page === 1 && bodyRef.current) {
            bodyRef.current.scrollTop = 0; // 스크롤 최상단
        }
    }, [page]);

    return (
        <div className="relative scrollbar-hidden">
            <div
                id="tabulator2"
                className="mt-3 table-report table-report--tabulator payList tabulator"
                role="grid"
                tabulator-layout="fitColumns"
            >
                <div className="tabulator-tableHolder relative" style={{ maxHeight: nonePage ? "" : "100%" }}>
                    {/* 헤더 영역 */}
                    <div style={{ overflowX: "hidden" }} ref={headerRef}>
                        <Listheader
                            tableHeaderData={tableHeaderData}
                            Width={Width}
                            extraWidth={extraWidth}
                            tableWidth={tableWidth}
                            setTableWidth={setTableWidth}
                        />
                    </div>
                    {/* 바디 영역 */}
                    <div style={{ overflow: "auto", height: height ? height : "auto" }} ref={bodyRef}>
                        {
                            spinner
                                ?
                                <div className='spinner-modal'>
                                    <SyncLoader color="#003577" />
                                </div>
                                :
                                <Listbody
                                    filter_tableBodyData={filter_tableBodyData}
                                    extraWidth={extraWidth}
                                    onInputChange={onInputChange}
                                    // 체크박스
                                    checkedRows={checkedRows}
                                    onCheckboxChange={onCheckboxChange}
                                    // 배경색
                                    check50={check50}
                                    check100={check100}
                                    check200={check200}
                                    selectedData={clickData}
                                    onRowClick={onRowClick}
                                    setCoordinate={setCoordinate}
                                    onClickCoordinate={onClickCoordinate}
                                    observerRef={observerRef}
                                />
                        }
                    </div>
                </div>

                {/* 페이지네이션 (예시로 남겨둔 주석) */}
                {nonePage && (
                    <Page
                        initialData={tableBodyData}
                        page={page}
                        setPage={setPage}
                        maxFirstPage={maxFirstPage}
                    />
                )}

                {/* 스크롤 표시 아이콘 */}
                {scrollState && isScrollable && !atBottom && !spinner && (
                    <div
                        className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center"
                        style={{ bottom: "-8px" }}
                    >
                        <ChevronDown
                            className="text-gray-400 text-2xl animate-bounce"
                            size={30}
                            style={{ color: "#000" }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function Listheader({ tableHeaderData, Width, extraWidth, tableWidth, setTableWidth }) {
    const ref = useRef();

    useEffect(() => {
        if (ref.current) {
            setTableWidth(`${ref.current.offsetWidth}` ?? "100%");
        }
    }, [setTableWidth]);

    const containerWidth = Number(Width) > Number(tableWidth) ? Width : tableWidth;

    return (
        <div
            className="tabulator-header"
            tabIndex="0"
            style={{
                paddingRight: "0px",
                marginLeft: "0px",
                width: `${containerWidth}px`,
                // position: "sticky",
                // top: "-1px",
                // zIndex: "10",
            }}
        >
            <div
                className="tabulator-headers tabulator-table"
                style={{ marginLeft: "0px" }}
                ref={ref}
            >
                {tableHeaderData.map((a, i) => (
                    <TableHeaderList key={i} item={a} extraWidth={extraWidth} />
                ))}
            </div>
        </div>
    );
}

function TableHeaderList({ item, extraWidth }) {
    let wid = item?.width?.split("px");
    const calcWidth = wid?.[0] ? Number(wid[0]) + extraWidth : 100;

    return (
        <div
            className="tabulator-col tabulator-sortable"
            role="columnheader"
            aria-sort="none"
            style={{
                minWidth: calcWidth + "px",
                width: calcWidth + "px",
                // height: "44px",
            }}
        >
            <div className="tabulator-col-content">
                <div className="tabulator-col-title-holder">
                    <div
                        className="tabulator-col-title"
                        style={{ textAlign: item?.textAlign ?? "center" }}
                    >
                        {item?.text ?? ""}
                    </div>
                    <div className="tabulator-col-sorter">
                        <div className="tabulator-arrow"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Listbody({
    filter_tableBodyData = [],
    extraWidth,
    onInputChange,
    // 체크박스
    checkedRows,
    onCheckboxChange,
    // 배경색 변경 조건
    check50 = false,
    check100 = false,
    check200 = false,
    selectedData,
    onRowClick,
    setCoordinate,
    onClickCoordinate,
    observerRef,
}) {
    // (A) 리스트가 없을 때 표시할 부분
    if (!filter_tableBodyData.length) {
        return (
            <div
                tabIndex="0"
                style={{
                    minHeight: "calc(100% - 84px)",
                    height: "auto",
                    maxHeight: "calc(100% - 84px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    style={{
                        color: "#666",
                        margin: "70px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        flexDirection: "column",
                    }}
                >
                    <FileX size={50} />
                    <p>리스트가 없습니다.</p>
                </div>
            </div>
        );
    }

    // (B) 리스트가 있을 때 기존 로직
    return (
        <div
            tabIndex="0"
            style={{
                minHeight: "calc(100% - 84px)",
                height: "auto",
                maxHeight: "calc(100% - 84px)",
            }}
        >
            <div
                className="tabulator-table"
                style={{ paddingTop: "0px", paddingBottom: "0px" }}
            >
                {filter_tableBodyData.map((rowArr, rowIndex) => {
                    let rowStyle = {};
                    let rowClass = "tabulator-row tabulator-selectable tabulator-row-odd";

                    if (rowArr.cancel_date) {
                        rowStyle.backgroundColor = "#fbdbdb";
                        rowClass += " canceled-row";
                    }
                    // (1) 선택된 행인지 판단
                    const selId = selectedData?.id ?? 0;
                    if (rowArr.id === selId) {
                        rowStyle.backgroundColor = "rgb(69 69 69)";
                        rowStyle.color = "#fff";
                        rowClass += " clickData";
                    }
                    // (2) 만약 오늘 날짜 데이터라면, 배경색 지정
                    //     (선택된 행일 경우(위 if문)에는 override 되므로 else if나 별도 로직으로 처리)
                    if (rowArr.isToday) {
                        rowStyle.backgroundColor = "#fff4ce";
                        // 필요 시 글자색 등 추가 가능
                    }


                    return (
                        <div
                            key={rowIndex}
                            className={rowClass}
                            role="row"
                            style={{
                                paddingLeft: "0px",
                                cursor: onRowClick ? "pointer" : "default",
                                ...rowStyle,
                            }}
                            onClick={() => onRowClick?.(rowArr)}
                        >
                            {rowArr.map((item, cellIndex) => (
                                <TableBodyList
                                    key={cellIndex}
                                    item={item}
                                    extraWidth={extraWidth}
                                    rowIndex={rowIndex}
                                    cellIndex={cellIndex}
                                    onInputChange={onInputChange}
                                    // 체크박스
                                    checkedRows={checkedRows}
                                    onCheckboxChange={onCheckboxChange}
                                    setCoordinate={setCoordinate}
                                    onClickCoordinate={onClickCoordinate}
                                />
                            ))}
                        </div>
                    );
                })}
                <div ref={observerRef} style={{ height: '1px' }}></div>
            </div>
        </div>
    );
}

function TableBodyList({
    item,
    extraWidth,
    rowIndex,
    cellIndex,
    onInputChange,

    // 체크박스
    checkedRows,
    onCheckboxChange,
    setCoordinate,
    onClickCoordinate,
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [isComposing, setIsComposing] = useState(false);

    const MyData = useSelector((state) => state.MyData);

    let wid = item?.width?.split("px");
    const calcWidth = wid?.[0] ? Number(wid[0]) + extraWidth : 100;

    const commonStyle = {
        width: calcWidth + "px",
        textAlign: item?.textAlign ?? "center",
        // display: "inline-flex",
        // alignItems: "flex-start",
        display: "flex",
        alignItems: "center",
        justifyContent:
            item.textAlign === "left"
                ? "flex-start"
                : item.textAlign === "center"
                    ? "center"
                    : item.textAlign === "right"
                        ? "flex-end"
                        : "center",
        height: MyData?.role == "SUPER" ? "34px" : "32px",
        overflow: item?.visible ? "visible" : ""
    };

    const handleDoubleClick = () => {
        if (item.type === "editable") {
            setIsEditing(true);
        }
    };

    const handleChange = (e) => {
        if (isComposing) return;
        onInputChange?.(rowIndex, cellIndex, e.target.value);
    };

    const handleClick = (e) => {
        const rect = e.target.getBoundingClientRect();
        setCoordinate(rect.top);
        onClickCoordinate();
    };

    // 체크박스 열
    if (item.type === "checkbox") {
        const isChecked = checkedRows.includes(rowIndex);
        return (
            <div
                className="tabulator-cell table-overflow-hidden"
                role="gridcell"
                style={{ ...commonStyle, justifyContent: "center" }}
            >
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => onCheckboxChange(rowIndex, e.target.checked)}
                    style={{ border: "1px solid #afafaf" }}
                />
            </div>
        );
    }

    // 일반 텍스트 열
    if (item.type !== "editable") {
        return (
            <div
                className="tabulator-cell table-overflow-hidden"
                role="gridcell"
                style={commonStyle}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
            >
                {typeof item?.text === "string" ? item.text : item.text ?? ""}
            </div>
        );
    }

    // 편집 가능 열
    if (!isEditing) {
        return (
            <div
                className="tabulator-cell table-overflow-hidden"
                role="gridcell"
                style={commonStyle}
                onDoubleClick={handleDoubleClick}
                onClick={handleClick}
            >
                {typeof item?.text === "string" ? item.text : item.text ?? ""}
            </div>
        );
    } else {
        // 편집 모드 (IME)
        return (
            <input
                className="tabulator-cell table-overflow-hidden"
                role="gridcell"
                style={{ ...commonStyle, backgroundColor: "yellow" }}
                value={typeof item?.text === "string" ? item.text : ""}
                onClick={handleClick}
                onChange={handleChange}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={(e) => {
                    setIsComposing(false);
                    onInputChange?.(rowIndex, cellIndex, e.target.value);
                }}
            />
        );
    }
}

export default List;